import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import type { ObservableQueryFields } from '@apollo/client';
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import { NextRouter } from 'next/router';
import { useRouter } from 'next/compat/router';
import { useObserver } from 'mobx-react-lite';

import { useStores } from 'contexts/stores';
import useErnie from 'shared/hooks/use-ernie';
import { parsePlaidMetaData } from 'src/payments/utils/parse-meta-data';
import completeAggregatorLink from 'src/payments/graphql/mutations/complete-aggretator-link.gql';
import { useDutchiePay, WhiteLabelConfig } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { getLogger } from 'src/utils/datadog-logger';

import {
  AGGREGATORS,
  ConnectedBankAccountDetails,
  DUTCHIE_PAY_QUERY_PARMAS_KEY,
  EnrollmentSteps,
  LOG_NAMESPACES,
  PLAID_OAUTH_PATH,
  PLAID_STORAGE_KEY,
  PlaidLinkCustomizationNames,
} from 'src/payments/constants';
import {
  ENROLLMENT_EVENTS,
  EVENT_ACTIONS,
  useDutchiePayAnalytics,
  DutchiePayEventTracker,
  PLAID_LINK_TO_BANK_CONNECTION_CONTEXT,
} from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import { formatPaymentsError, getGraphQLReasonCode } from 'src/payments/hooks/use-payments-error/use-payments-error';
import type { StartAggregatorLinkV2Query, StartAggregatorLinkV2QueryVariables } from 'src/gql/graphql';

type StartAggregatorLinkV2Refetch = ObservableQueryFields<
  StartAggregatorLinkV2Query,
  StartAggregatorLinkV2QueryVariables
>['refetch'];

const logger = getLogger(LOG_NAMESPACES.PBB_ENROLLMENT);

type SuccessConfig = {
  publicToken: string;
  metadata: PlaidLinkOnSuccessMetadata;
};

export type OnSuccessArgs = {
  apolloClient: any;
  clearPlaidData: () => void;
  completeAggregatorLinkStarted: () => void;
  completeAggregatorLinkSucceeded: (details: ConnectedBankAccountDetails) => void;
  dutchiePayEventTracker: DutchiePayEventTracker;
  handleEmbeddedDutchiePayLink?: () => void;
  handleOnError?: () => void;
  isOAuthRedirect: boolean;
  metadata: PlaidLinkOnSuccessMetadata;
  paymentMethodId?: string;
  aggregatorLinkExited: () => void;
  accountChurnDetected: () => void;
  publicToken: string;
  queryParams: string;
  router: NextRouter | null;
  showErnie: (
    msg?: string | undefined,
    type?: 'danger' | 'error' | 'info' | 'success' | undefined,
    timeout?: number | undefined,
    reasonCode?: string | undefined
  ) => void;
  userFullName: string;
  userId: string;
  linkCustomizationName: PlaidLinkCustomizationNames;
  whiteLabelConfig: WhiteLabelConfig;
  dispensaryName?: string;
  dispensaryId?: string;
  handleOnPlaidConnectBank?: () => void;
  refetchStartAggregatorLink?: StartAggregatorLinkV2Refetch;
};

type PlaidLinkUtils = {
  handleSuccess: ({ publicToken, metadata }: SuccessConfig) => Promise<void>;
};

type UsePlaidLinkUtilsParams = {
  handleEmbeddedDutchiePayLink?: () => void;
  handleOnError?: () => void;
  linkCustomizationName: PlaidLinkCustomizationNames;
  handleOnPlaidConnectBank?: () => void;
  refetchStartAggregatorLink?: StartAggregatorLinkV2Refetch;
};

export const onSuccess = async ({
  apolloClient,
  clearPlaidData,
  completeAggregatorLinkStarted,
  completeAggregatorLinkSucceeded,
  dutchiePayEventTracker,
  handleEmbeddedDutchiePayLink,
  handleOnError,
  isOAuthRedirect,
  metadata,
  aggregatorLinkExited,
  accountChurnDetected,
  publicToken,
  queryParams,
  router,
  showErnie,
  userFullName,
  userId,
  linkCustomizationName,
  whiteLabelConfig,
  dispensaryName,
  dispensaryId,
  handleOnPlaidConnectBank,
  refetchStartAggregatorLink,
}: OnSuccessArgs): Promise<void> => {
  completeAggregatorLinkStarted();
  const formattedMetaData = parsePlaidMetaData({ metadata });

  try {
    handleOnPlaidConnectBank?.();
    const response = await apolloClient.mutate({
      mutation: completeAggregatorLink,
      variables: {
        aggregatorToken: publicToken,
        aggregator: AGGREGATORS.PLAID,
        plaid: { ...formattedMetaData },
        paymentMethodId: '',
        dispensaryName,
        dispensaryId,
      },
    });

    const pendingPaymentMethod = response.data.completeAggregatorLink.paymentMethods.filter(
      (method) => method.status === 'pending'
    );

    if (!pendingPaymentMethod || pendingPaymentMethod.length === 0) {
      throw new Error('No pending payment method found');
    }

    completeAggregatorLinkSucceeded({
      lastDigits: metadata.accounts[0].mask,
      bankName: metadata.institution?.name ?? '',
      id: pendingPaymentMethod[0].id,
      ownerName: userFullName,
      accountName: metadata.accounts[0].name,
    });

    dutchiePayEventTracker(ENROLLMENT_EVENTS.ENROLLMENT_STEP, {
      step: EnrollmentSteps.connectBank,
      action: EVENT_ACTIONS.SUCCEEDED,
      context: PLAID_LINK_TO_BANK_CONNECTION_CONTEXT[linkCustomizationName],
    });

    handleEmbeddedDutchiePayLink?.();

    if (isOAuthRedirect && queryParams) {
      try {
        const parsedQueryParams = JSON.parse(queryParams);
        if (parsedQueryParams && typeof parsedQueryParams === 'object') {
          void router?.replace({
            query: { ...router.query, ...parsedQueryParams },
          });
        }
      } catch (parseError) {
        logger.error('PlaidLinkUtils: Invalid OAuth query params JSON', {
          error: parseError,
          value: queryParams,
          dispensaryId,
          dispensaryName,
          userId,
          step: 'oauthParamsParsing',
          linkCustomizationName,
        });
      }
    }
  } catch (err) {
    logger.error('PlaidLinkUtils: Failed to complete aggregator link', {
      error: err,
      dispensaryId,
      dispensaryName,
      userId,
      step: 'connectBank',
      linkCustomizationName,
      plaid: { ...formattedMetaData },
    });
    const reasonCode = getGraphQLReasonCode(err);

    if (reasonCode === 'E006') {
      logger.warn('PlaidLinkUtils: Plaid account churn detected (E006)', {
        dispensaryId,
        dispensaryName,
        userId,
        step: 'connectBank',
        linkCustomizationName,
        plaid: { ...formattedMetaData },
      });
      accountChurnDetected();
    } else {
      const { errorMarkdown, ernieType, ernieTimeout } = formatPaymentsError(
        err,
        whiteLabelConfig.brandedDutchiePayName
      );
      showErnie(errorMarkdown, ernieType, ernieTimeout);
      handleOnError?.();
      aggregatorLinkExited();
      void refetchStartAggregatorLink?.();
    }
  } finally {
    clearPlaidData();
  }
};

export const usePlaidLinkUtils = ({
  handleEmbeddedDutchiePayLink,
  handleOnError,
  linkCustomizationName,
  handleOnPlaidConnectBank,
  refetchStartAggregatorLink,
}: UsePlaidLinkUtilsParams): PlaidLinkUtils => {
  const apolloClient = useApolloClient();
  const queryParams = localStorage.getItem(DUTCHIE_PAY_QUERY_PARMAS_KEY);

  const {
    completeAggregatorLinkSucceeded,
    aggregatorLinkExited,
    completeAggregatorLinkStarted,
    accountChurnDetected,
  } = useDutchiePayEnrollment();
  const { paymentMethodId, whiteLabelConfig } = useDutchiePay();
  const { dutchiePayEventTracker } = useDutchiePayAnalytics();
  const { dispensary } = useDispensary();

  const router = useRouter();
  const isOAuthRedirect = router?.asPath.includes(PLAID_OAUTH_PATH) ?? false;

  const { User } = useStores();
  const userFullName = useObserver(() => User.fullName);
  const userId = useObserver(() => User.id);
  const dispensaryName = dispensary?.name;
  const dispensaryId = dispensary?.id;

  const showErnie = useErnie();

  const clearPlaidData = (): void => {
    localStorage.removeItem(DUTCHIE_PAY_QUERY_PARMAS_KEY);
    localStorage.removeItem(PLAID_STORAGE_KEY);
  };

  const handleSuccess: ({ publicToken, metadata }) => Promise<void> = useCallback(
    ({ publicToken, metadata }) =>
      onSuccess({
        apolloClient,
        clearPlaidData,
        completeAggregatorLinkStarted,
        completeAggregatorLinkSucceeded,
        dutchiePayEventTracker,
        handleEmbeddedDutchiePayLink,
        handleOnError,
        isOAuthRedirect,
        metadata,
        paymentMethodId,
        aggregatorLinkExited,
        accountChurnDetected,
        publicToken,
        queryParams: queryParams ?? '',
        router,
        showErnie,
        userFullName,
        userId,
        linkCustomizationName,
        whiteLabelConfig,
        dispensaryName,
        dispensaryId,
        handleOnPlaidConnectBank,
        refetchStartAggregatorLink,
      }),
    [
      apolloClient,
      completeAggregatorLinkStarted,
      completeAggregatorLinkSucceeded,
      dutchiePayEventTracker,
      handleEmbeddedDutchiePayLink,
      handleOnError,
      isOAuthRedirect,
      paymentMethodId,
      aggregatorLinkExited,
      accountChurnDetected,
      queryParams,
      router,
      showErnie,
      userFullName,
      userId,
      linkCustomizationName,
      whiteLabelConfig,
      dispensaryName,
      dispensaryId,
      handleOnPlaidConnectBank,
      refetchStartAggregatorLink,
    ]
  );

  return {
    handleSuccess,
  };
};
